import { useFormik } from "formik";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import {
  CreateBannerRequest,
  BannerDto,
  State,
  UpdateBannerRequest,
} from "../../interfaces";
import {
  addBanner,
  getBanner,
  updateBanner,
  uploadBannerImage,
} from "../../store/actions";
import { ActivityButton } from "../../components/Common";
import { IMAGE_ACCEPT } from "../../constants";
import { ToastContainer, toast } from "react-toastify";
import { MediaDto } from "../../interfaces/media";
import profile from "../../assets/images/profile.png";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const AddEditBannerModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [request] = useState<CreateBannerRequest>();

  const { error: getBannerError, response: banner } = useSelector(
    (state: any): State<BannerDto> => state.banner.banner
  );

  const { loading, success, error } = useSelector(
    (state: any): State<CreateBannerRequest> => state.banner.addBanner
  );

  const {
    loading: updateBannerLoading,
    success: updateBannerSuccess,
    error: updateBannerError,
  } = useSelector(
    (state: any): State<UpdateBannerRequest> => state.banner.updateBanner
  );

  const {
    error: imageUploadingError,
    loading: uploadingImage,
    response: uploadedImage,
    success: uploadImageSuccess,
  } = useSelector((state: any): State<MediaDto> => state.banner.bannerImage);

  //#region form
  const form = useFormik<CreateBannerRequest>({
    enableReinitialize: true,
    initialValues: {
      title: {
        en: request?.title.en || "",
        ar: request?.title.ar || "",
      },
      description: {
        en: request?.description.en || "",
        ar: request?.description.ar || "",
      },
      imageId: request?.imageId,
      redirectUrl: request?.redirectUrl || null,
    },
    initialErrors: error || updateBannerError,
    validationSchema: Yup.object({
      title: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" })
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            })
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "arabicValue" })
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            })
          ),
      }),
      description: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" })
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            })
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "arabicValue" })
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            })
          ),
      }),
      redirectUrl: Yup.string()
        .optional()
        .nullable()
        .url(
          t("mustBeUrl", {
            displayName: "redirectUrl",
          })
        ),
    }),
    onSubmit: (values) => {
      if (id) {
        dispatch(
          updateBanner(
            id,
            {
              ...values,
              redirectUrl:
                values.redirectUrl === "" ? null : values.redirectUrl,
            },
            history
          )
        );
      } else {
        dispatch(
          addBanner(
            {
              ...values,
              redirectUrl:
                values.redirectUrl === "" ? null : values.redirectUrl,
            },
            history
          )
        );
      }
    },
  });
  //#endregion

  //#region effects
  useEffect(() => {
    if (
      (!loading && success) ||
      (!updateBannerLoading && updateBannerSuccess)
    ) {
      onClose();
    }
  }, [loading, updateBannerLoading]);

  useEffect(() => {
    if (!uploadingImage && uploadImageSuccess && uploadedImage) {
      form.setFieldValue("imageId", uploadedImage.id);
    }
  }, [uploadingImage]);

  useEffect(() => {
    if (id) {
      dispatch(getBanner(id, history));
    }
  }, [id]);

  useEffect(() => {
    if (id && banner) {
      form.setValues({
        title: banner.title,
        description: banner.description,
        redirectUrl: banner.redirectUrl || null,
      });
    }
  }, [banner]);

  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
  };

  const onChangeBannerImage = (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const { files } = target;
    if (files && files.length > 0) {
      const imageFile = files[0];
      if (
        !imageFile.name.match(
          /\.(jpg|JPG|jpeg|JPEG|jfif|pjpeg|pjp|png|PNG|webp|gif|GIF)$/
        )
      ) {
        toast.error(t("selectValidImage"), {
          autoClose: 3000,
        });
        return false;
      }
      dispatch(uploadBannerImage(imageFile, history, banner?.id));
    }
  };

  //#endregion

  return (
    <React.Fragment>
      <ToastContainer />
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
        className="modal-md"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="p-3"
            toggle={() => {
              onClose();
            }}
          >
            {id
              ? t("editItem", { item: "image" })
              : t("addItem", { item: "image" })}
          </ModalHeader>
          {typeof error === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{error}</p>
            </Alert>
          )}
          {typeof getBannerError === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{getBannerError}</p>
            </Alert>
          )}
          {typeof updateBannerError === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{updateBannerError}</p>
            </Alert>
          )}
          {typeof imageUploadingError === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{imageUploadingError}</p>
            </Alert>
          )}
          <ModalBody>
            <Row>
              <Col sm={12}>
                {/* Question Image */}
                <div className="col-auto text-center">
                  <div className="profile-user position-relative d-inline-block mx-auto mb-4">
                    <img
                      src={
                        uploadedImage?.path || banner?.image?.path || profile
                      }
                      className="rounded-circle avatar-xl img-thumbnail user-profile-image"
                      alt="user-profile"
                    />
                    <div className="avatar-xl p-0 rounded-circle profile-photo-edit">
                      <Input
                        id="banner_image"
                        type="file"
                        className="profile-img-file-input"
                        accept={IMAGE_ACCEPT}
                        onChange={onChangeBannerImage}
                        disabled={uploadingImage}
                      />
                      <Label
                        htmlFor="banner_image"
                        className="profile-photo-edit avatar-xs"
                      >
                        <span className="avatar-title rounded-circle bg-light text-body">
                          {uploadingImage && <Spinner />}
                          {!uploadingImage && (
                            <i className="ri-camera-fill"></i>
                          )}
                        </span>
                      </Label>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            <div className="mb-3">
              <label htmlFor="title.en" className="form-label">
                {t("titleEn")}
                <span className="text-danger">*</span>
              </label>
              <Input
                disabled={getBannerError}
                autoComplete="off"
                auto
                id="title.en"
                name="title.en"
                value={form.values.title?.en}
                type="text"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.title?.en && form.errors.title?.en ? true : false
                }
              />
              {form.touched.title?.en && form.errors.title?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.title?.en as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="title.ar" className="form-label">
                {t("nameAr")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ fontFamily: "aljannat", direction: "rtl" }}
                disabled={getBannerError}
                autoComplete="off"
                auto
                id="title.ar"
                name="title.ar"
                value={form.values.title?.ar}
                type="text"
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.title?.ar && form.errors.title?.ar ? true : false
                }
              />
              {form.touched.title?.ar && form.errors.title?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.title?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="description.en" className="form-label">
                {t("descriptionEn")}
                <span className="text-danger">*</span>
              </label>
              <Input
                disabled={getBannerError}
                autoComplete="off"
                auto
                id="description.en"
                name="description.en"
                value={form.values.description?.en}
                type="text"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.description?.en && form.errors.description?.en
                    ? true
                    : false
                }
              />
              {form.touched.description?.en && form.errors.description?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.description?.en as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="description.ar" className="form-label">
                {t("descriptionAr")}
                <span className="text-danger">*</span>
              </label>
              <Input
                disabled={getBannerError}
                autoComplete="off"
                auto
                id="description.ar"
                name="description.ar"
                value={form.values.description?.ar}
                type="text"
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.description?.ar && form.errors.description?.ar
                    ? true
                    : false
                }
              />
              {form.touched.description?.ar && form.errors.description?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.description?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="redirectUrl" className="form-label">
                {t("redirectUrl")}
              </label>
              <Input
                disabled={getBannerError}
                autoComplete="off"
                auto
                id="redirectUrl"
                name="redirectUrl"
                value={form.values.redirectUrl || ""}
                type="text"
                className="form-control"
                placeholder={`${t("default")}: https://theq-app.com`}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.redirectUrl && form.errors.redirectUrl
                    ? true
                    : false
                }
              />
              {form.touched.redirectUrl && form.errors.redirectUrl ? (
                <FormFeedback type="invalid">
                  {form.errors.redirectUrl as string}
                </FormFeedback>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={loading || updateBannerLoading}
              disabled={
                loading ||
                updateBannerLoading ||
                getBannerError ||
                uploadingImage
              }
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
